import openai from '../../../lib/openaiClient';

/**
 * Generates AI-powered buying guide content
 * @param {Object} params - Guide parameters
 * @returns {Promise<Object>} Generated buying guide content
 */
export async function generateBuyingGuide(params) {
  try {
    const { title, category, targetAudience, focusPoints, priceRange, seasonalRelevance } = params;

    const prompt = `Create a comprehensive buying guide for vacuum cleaners with the following specifications:

Title: ${title}
Category: ${category}
Target Audience: ${targetAudience}
${focusPoints ? `Focus Points: ${focusPoints}` : ''}
${priceRange ? `Price Range: ${priceRange}` : ''}
${seasonalRelevance ? `Seasonal Relevance: ${seasonalRelevance}` : ''}

Generate a complete buying guide with:

1. ENGAGING INTRODUCTION (150-200 words)
   - Hook the reader with a relatable scenario
   - Explain why this guide is valuable
   - Preview what they'll learn

2. MAIN CONTENT (1500-2000 words)
   - Key factors to consider when buying
   - Detailed explanations of important features
   - Common mistakes to avoid
   - Expert recommendations
   - Comparison of different options
   - Real-world use cases and scenarios

3. PRODUCT RECOMMENDATIONS
   - Top 3-5 product suggestions with brief explanations
   - Why each product is recommended
   - Best use cases for each

4. BUYING TIPS
   - When to buy (seasonal considerations, sales events)
   - Where to buy (online vs retail)
   - Warranty and return policy considerations
   - Budget optimization strategies

5. FREQUENTLY ASKED QUESTIONS
   - 5-7 common questions with detailed answers

6. CONCLUSION (100-150 words)
   - Summarize key takeaways
   - Final recommendations
   - Call to action

Write in a friendly, authoritative tone. Use clear headings and bullet points. Focus on practical, actionable advice that helps readers make informed decisions.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert consumer product advisor and content writer specializing in home appliances and vacuum cleaners. You write comprehensive, SEO-optimized buying guides that combine technical knowledge with practical consumer insights. Your guides are trusted by thousands of readers making purchasing decisions.' 
        },
        { role: 'user', content: prompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'buying_guide',
          schema: {
            type: 'object',
            properties: {
              title: { type: 'string' },
              summary: { type: 'string' },
              content: { type: 'string' },
              keyPoints: {
                type: 'array',
                items: { type: 'string' },
                minItems: 4,
                maxItems: 8
              },
              recommendedProducts: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    name: { type: 'string' },
                    reason: { type: 'string' },
                    bestFor: { type: 'string' }
                  },
                  required: ['name', 'reason', 'bestFor']
                }
              },
              seoTitle: { type: 'string' },
              seoDescription: { type: 'string' },
              seoKeywords: {
                type: 'array',
                items: { type: 'string' }
              },
              readTimeMinutes: { type: 'number' }
            },
            required: ['title', 'summary', 'content', 'keyPoints', 'recommendedProducts', 'seoTitle', 'seoDescription', 'seoKeywords', 'readTimeMinutes'],
            additionalProperties: false
          }
        }
      },
    });

    const guideContent = JSON.parse(response?.choices?.[0]?.message?.content);
    return guideContent;
  } catch (error) {
    console.error('Error generating buying guide:', error);
    throw error;
  }
}